import {
  faBookMedical,
  faEdit,
  faExclamationTriangle,
  faEyeSlash,
  faGripVertical,
  faPlus,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { Component } from "react";
import {
  Button,
  ButtonGroup,
  FormCheck,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
  Nav,
  NavItem,
  NavLink
} from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FormattedMessage, injectIntl } from "react-intl";
import { ADMIN, TEMPLATE_CREATOR } from "../../../common/constant";
import { isSectionLibraryEnabled } from "../../../common/runtimeConfig";
import SaveSectionModal from "./sectionLibrary/SaveSectionModal";
import { isPageKeyDuplicated } from "../../../ducks/surveyEditor";
import ObjectUtils from "../../../utils/ObjectUtils";
import IconTooltip from "../../common/IconTooltip";
import Visibility from "../../common/VisibleContainer";

/**
 * Header for tabs component responsible for adding/updating/removing
 * tab information as well as changing the current tab.
 */
class TabHeader extends Component {
  constructor(props) {
    super(props);
    this.state = { librarySection: null, confirmingDeletion: false };
    this.onConfirmRemove = this.onConfirmRemove.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleOpenDialog = this.handleOpenDialog.bind(this);
    this.handleCloseDialog = this.handleCloseDialog.bind(this);
    this.onNameChange = this.onNameChange.bind(this);
    this.onDescriptionChange = this.onDescriptionChange.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onRemovePage = this.onRemovePage.bind(this);
    this.onConfirmEditPage = this.onConfirmEditPage.bind(this);
  }

  onConfirmEditPage() {
    const {
      prepareDataForPageEditing,
      editPage,
      selectedPage,
      editingPageTitle,
      editingPageKey,
      editingPageDescription,
      editingPageVisibility
    } = this.props;
    prepareDataForPageEditing("", "", "", "", true);
    editPage(selectedPage, editingPageTitle, editingPageKey, editingPageDescription, editingPageVisibility);
  }

  onRemovePage() {
    const { selectedPage, showDialog } = this.props;
    this.setState({ confirmingDeletion: true }, () =>
      showDialog(
        { selectedPage },
        this.onConfirmRemove,
        "survey.editor.confirm.page.deletion",
        "survey.editor.confirm.page.deletion",
        "confirmDialog.confirmLabel",
        "confirmDialog.dismissLabel",
        undefined,
        () => this.setState({ confirmingDeletion: false })
      )
    );
  }

  onConfirmRemove(data, links) {
    const { removePage, prepareDataForPageEditing } = this.props;
    removePage(data, links);
    prepareDataForPageEditing("", "", "", "", true);
    this.setState({ confirmingDeletion: false });
  }

  onNameChange(event) {
    const { editorPropertyChange } = this.props;
    editorPropertyChange("editingPageTitle", event.target.value);
  }

  onKeyChange(event) {
    const { editorPropertyChange } = this.props;
    editorPropertyChange("editingPageKey", event.target.value);
  }

  onDescriptionChange(description) {
    const { editorPropertyChange } = this.props;
    editorPropertyChange("editingPageDescription", description);
  }

  handleOpenDialog(page) {
    const { editorPropertyChange, selectedPage, prepareDataForPageEditing } = this.props;
    editorPropertyChange("showModalPageEditor", selectedPage);
    prepareDataForPageEditing(
      selectedPage,
      page.title ? page.title : page.name,
      page.key,
      page.description,
      page.visible === undefined ? true : page.visible
    );
  }

  handleCloseDialog() {
    const { prepareDataForPageEditing } = this.props;
    prepareDataForPageEditing("", "", "", "", true);
  }

  onVisibilityChange() {
    const { editorPropertyChange, editingPageVisibility } = this.props;
    editorPropertyChange("editingPageVisibility", !editingPageVisibility);
  }

  handleDragStart(pageName, event) {
    const dragData = JSON.stringify({
      type: "reorderPage",
      pageName: pageName
    });
    event.dataTransfer.setData("text", dragData);
    event.stopPropagation();
  }

  handleDrop(toBeReplaced, event) {
    const { reorderPage, moveQuestionToPage } = this.props;

    let dragData = JSON.parse(event.dataTransfer.getData("text"));
    if ("reorderPage" === ObjectUtils.getDeepProperty(dragData, "type")) {
      reorderPage(dragData.pageName, toBeReplaced);
    } else {
      //question name which needs to me moved and name of page on which it is moving
      moveQuestionToPage(dragData.questionName, toBeReplaced);
    }
  }

  render() {
    const {
      intl,
      pages,
      selectedPage,
      showModalPageEditor,
      editingPageTitle,
      editingPageKey,
      editingPageDescription,
      editingPageVisibility,
      addPage
    } = this.props;

    return (
      <>
        {isSectionLibraryEnabled() && this.state.librarySection && (
          <SaveSectionModal
            section={this.state.librarySection}
            onHide={() => this.setState({ librarySection: null })}
          />
        )}
        <Nav className="gap-sm my-3">
          {pages.map(function (page, i) {
            const pageTitle = page.title ? page.title : page.name;

            const tooltip = (
              <Tooltip className="survey-tooltip" id="tooltip">
                {pageTitle}
              </Tooltip>
            );
            const active = selectedPage === page.name;
            const pageKeyDuplicated = isPageKeyDuplicated({ pages }, page.key);

            return (
              <NavItem
                key={i}
                as={ButtonGroup}
                className={"rounded " + (active ? "bg-primary" : "bg-light")}
                style={{ maxWidth: 200 }}
                onDrop={(e) => this.handleDrop(page.name, e)}
              >
                <OverlayTrigger placement="top" overlay={tooltip}>
                  <NavLink
                    className={"d-flex align-items-center flex-grow-1 " + (!active ? "text-primary" : "text-light")}
                    style={{ minWidth: 0 }}
                    draggable
                    onDragStart={(e) => this.handleDragStart(page.name, e)}
                    eventKey={page.name}
                  >
                    <FontAwesomeIcon icon={faGripVertical} style={{ cursor: "grab" }} className="me-2 flex-shrink-0" />
                    <span className="text-truncate" style={{ minWidth: 0 }}>
                      {pageTitle}
                    </span>
                    {!page.visible && <FontAwesomeIcon icon={faEyeSlash} className="ms-2 flex-shrink-0" />}
                    {pageKeyDuplicated && (
                      <span className="ms-2 flex-shrink-0">
                        <IconTooltip
                          iconClass="text-danger"
                          iconDefinition={faExclamationTriangle}
                          tooltipMessageId="survey.version.compare.warning.duplicate.page.keys"
                        />
                      </span>
                    )}
                  </NavLink>
                </OverlayTrigger>
                {active && isSectionLibraryEnabled() && (
                  <Visibility roles={[TEMPLATE_CREATOR]}>
                    <Button
                      size="sm"
                      variant="link"
                      className={active ? "text-light" : "text-primary"}
                      title={intl.formatMessage({ id: "section.library.save" })}
                      aria-label={intl.formatMessage({ id: "section.library.save" })}
                      onClick={() => this.setState({ librarySection: ObjectUtils.deepClone(page) })}
                    >
                      <FontAwesomeIcon icon={faBookMedical} />
                    </Button>
                  </Visibility>
                )}
                {active && (
                  <Button
                    size="sm"
                    variant="link"
                    className={active ? "text-light" : "text-primary"}
                    onClick={() => this.handleOpenDialog(page)}
                    disabled={!selectedPage}
                    title={intl.formatMessage({ id: "survey.editor.tooltip.page.edit" })}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                )}
                {!this.state.confirmingDeletion && (
                  <Modal
                    size="lg"
                    show={showModalPageEditor === page.name && !this.state.librarySection}
                    onHide={() => this.handleCloseDialog()}
                    keyboard={true}
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>{intl.formatMessage({ id: "survey.editor.rename.page" })}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <FormGroup className="mb-3">
                        <FormLabel>
                          <FormattedMessage id="survey.editor.page.title" />
                        </FormLabel>
                        <FormControl
                          onChange={(e) => this.onNameChange(e)}
                          value={editingPageTitle ? editingPageTitle : ""}
                        />
                      </FormGroup>
                      <Visibility roles={[ADMIN]}>
                        <FormGroup className="mb-3">
                          <FormLabel>
                            <FormattedMessage id="survey.editor.page.key" />
                          </FormLabel>
                          <FormControl
                            onChange={(e) => this.onKeyChange(e)}
                            value={editingPageKey ? editingPageKey : ""}
                          />
                        </FormGroup>
                      </Visibility>
                      <FormGroup as="fieldset">
                        <FormLabel as="legend" className="fs-6">
                          <FormattedMessage id="survey.editor.page.visibility" />
                        </FormLabel>
                        <FormCheck
                          id={`section-visible-${page.name}`}
                          type="checkbox"
                          label={intl.formatMessage({ id: "survey.editor.page.visible" })}
                          checked={!!editingPageVisibility}
                          onChange={this.onVisibilityChange}
                        />
                      </FormGroup>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between gap-3">
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        {isSectionLibraryEnabled() && (
                          <Visibility roles={[TEMPLATE_CREATOR]}>
                            <Button
                              variant="secondary"
                              onClick={() =>
                                this.setState({
                                  librarySection: ObjectUtils.deepClone({
                                    ...page,
                                    title: editingPageTitle,
                                    key: editingPageKey,
                                    description: editingPageDescription,
                                    visible: editingPageVisibility
                                  })
                                })
                              }
                            >
                              <FontAwesomeIcon icon={faBookMedical} className="me-2" />
                              <FormattedMessage id="section.library.saveAction" />
                            </Button>
                          </Visibility>
                        )}
                        <Button variant="outline-danger" onClick={this.onRemovePage} disabled={pages.length === 1}>
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          <FormattedMessage id="app.common.action.delete" />
                        </Button>
                      </div>
                      <div className="d-flex align-items-center gap-sm ms-auto">
                        <Button onClick={() => this.handleCloseDialog()} variant="outline-secondary">
                          <FormattedMessage id="app.common.Cancel" />
                        </Button>
                        <Button onClick={() => this.onConfirmEditPage()} variant="primary">
                          <FormattedMessage id="survey.editor.save" />
                        </Button>
                      </div>
                    </Modal.Footer>
                  </Modal>
                )}
              </NavItem>
            );
          }, this)}

          <Button onClick={() => addPage()} title={intl.formatMessage({ id: "survey.editor.tooltip.page.add" })}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Nav>
      </>
    );
  }
}

TabHeader.propTypes = {
  showDialog: PropTypes.func,
  addPage: PropTypes.func,
  selectedPage: PropTypes.string,
  reorderPage: PropTypes.func,
  changeSelectedPage: PropTypes.func,
  removePage: PropTypes.func,
  editPage: PropTypes.func,
  prepareDataForPageEditing: PropTypes.func,
  pages: PropTypes.array,
  intl: PropTypes.object,
  showModalPageEditor: PropTypes.string,
  editingPageTitle: PropTypes.string,
  editingPageKey: PropTypes.string,
  editingPageDescription: PropTypes.string,
  editingPageVisibility: PropTypes.bool
};

export default injectIntl(TabHeader);
